import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import styled from "styled-components";
import RoundedImage from "../../RoundedImage";
import unknown_gw from "../../../assets/unknown_gw.png";
import {
  FiMenu, // hamburger
  FiSearch, // search
  FiUser,
  FiSettings,
  FiLogOut,
  FiGrid,
  FiHelpCircle,
  FiPlus, // create
  FiHome, // home
  FiMessageSquare, // messages
} from "react-icons/fi";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import {
  search,
  zipcode,
  distance,
  categories,
  sortBy,
  itemConditions,
  location,
  zipcodeDistChanged,
  updateUnreadCount,
} from "../../../store/actions";
import { request } from "../../../services/networking/request";
import { API_CONSTANT_MAP } from "../../../utils/constants/endpoints";
import useTasklyWS from "../../../hooks/useTasklyWS";
import VerificationBadge from "../../common/VerificationBadge";

/* ======================= DESIGN TOKENS ======================= */
const theme = {
  colors: {
    primary: "#3b82f6",
    primaryHover: "#2563eb",
    primaryLight: "#eff6ff",
    secondary: "#6366f1",
    textPrimary: "#0f172a",
    textSecondary: "#475569",
    textMuted: "#64748b",
    surface: "#ffffff",
    surfaceHover: "#f8fafc",
    background: "#ffffff",
    border: "#e2e8f0",
    borderLight: "#f1f5f9",
    accent: "#10b981",
    warning: "#f59e0b",
    error: "#ef4444",
  },
  gradients: {
    primary: "linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)",
    secondary: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
    surface: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
  },
  shadows: {
    xs: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
    sm: "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
    md: "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
    lg: "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
    navbar: "0 1px 3px 0 rgb(0 0 0 / 0.05), 0 1px 2px -1px rgb(0 0 0 / 0.05)",
  },
  spacing: {
    xs: "0.25rem",
    sm: "0.5rem",
    md: "0.75rem",
    lg: "1rem",
    xl: "1.5rem",
    xxl: "2rem",
  },
  borderRadius: {
    sm: "0.375rem",
    md: "0.5rem",
    lg: "0.75rem",
    xl: "1rem",
    full: "9999px",
  },
  typography: {
    fontFamily:
      "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    fontWeight: {
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
      extrabold: 800,
    },
  },
};

/* ======================= STYLES ======================= */
const NavWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 400000;
  background: ${theme.gradients.surface};
  backdrop-filter: blur(16px) saturate(180%);
  border-bottom: 1px solid ${theme.colors.borderLight};
  box-shadow: ${theme.shadows.navbar};
`;

const NavContainer = styled.nav`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 64px;
  padding: 0 ${theme.spacing.xl};
  font-family: ${theme.typography.fontFamily};

  @media (max-width: 1024px) {
    height: 56px;
    padding: 0 ${theme.spacing.lg};
  }

  @media (max-width: 640px) {
    padding: 0 ${theme.spacing.md};
  }
`;

const LogoSection = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  z-index: 10;

  @media (max-width: 1024px) {
    display: none;
  }
`;

const NavContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: ${theme.spacing.xxl};

  @media (max-width: 1024px) {
    gap: ${theme.spacing.lg};
  }
`;

const DesktopNav = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};

  @media (max-width: 1024px) {
    display: none;
  }
`;

const NavMenu = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  margin-right: ${theme.spacing.lg};
`;

const NavActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
`;

const IconContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  color: inherit;

  & > span:last-child {
    font-size: 0.75rem;
    font-weight: ${theme.typography.fontWeight.medium};
    color: inherit;
  }

  @media (max-width: 768px) {
    flex-direction: row;
    gap: ${theme.spacing.sm};
    width: 100%;
    justify-content: flex-start;

    & > span:last-child {
      font-size: 0.9rem;
      font-weight: ${theme.typography.fontWeight.semibold};
    }
  }
`;

const Brand = styled(Link)`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  text-decoration: none;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    transform: translateY(-1px);
  }
`;

const BrandIcon = styled.div`
  width: 32px;
  height: 32px;
  background: ${theme.gradients.primary};
  border-radius: ${theme.borderRadius.md};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: ${theme.typography.fontWeight.bold};
  color: white;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent
    );
    transition: left 0.5s;
  }

  ${Brand}:hover &::before {
    left: 100%;
  }

  @media (max-width: 640px) {
    width: 32px;
    height: 32px;
    font-size: 18px;
  }
`;

const BrandText = styled.h1`
  font-size: 1.5rem;
  font-weight: ${theme.typography.fontWeight.extrabold};
  color: ${theme.colors.textPrimary};
  margin: 0;
  letter-spacing: -0.025em;

  @media (max-width: 1024px) {
    font-size: 1.35rem;
  }
`;

const SidebarLogo = styled.h1`
  color: #fff;
  cursor: pointer;
  font-size: 1.6rem;
  font-weight: 800;
  letter-spacing: -0.03em;
  margin: 0;
  transition: all 0.3s ease;
  position: relative;
  display: flex;
  align-items: center;

  &:hover {
    transform: scale(1.05);
  }

  @media (max-width: 1024px) {
    font-size: 1.5rem;
    font-weight: 900;
  }
`;

const UserDropdown = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  min-width: 280px;
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.lg};
  padding: ${theme.spacing.sm};
  display: ${(props) => (props.visible ? "block" : "none")};
  z-index: 45;
  backdrop-filter: blur(12px);
`;

const DropdownHeader = styled.div`
  padding: ${theme.spacing.md};
  border-bottom: 1px solid ${theme.colors.borderLight};
  margin-bottom: ${theme.spacing.sm};
`;

const DropdownUserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
`;

const DropdownUserDetails = styled.div`
  flex: 1;
  min-width: 0;
`;

const DropdownUserName = styled.div`
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.textPrimary};
  font-size: 0.9rem;
  text-transform: capitalize;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const DropdownUserEmail = styled.div`
  font-size: 0.8rem;
  color: ${theme.colors.textMuted};
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const DropdownMenuItem = styled.a`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border-radius: ${theme.borderRadius.md};
  font-size: 0.875rem;
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.textSecondary};
  text-decoration: none;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    background-color: ${theme.colors.surfaceHover};
    color: ${theme.colors.textPrimary};
  }

  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }
`;

const NavActionButton = styled.button`
  cursor: pointer;
  display: flex;
  align-items: center;
  background: transparent;
  border: none;
  text-decoration: none;
  position: relative;
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  color: ${theme.colors.textSecondary};

  &:hover {
    background-color: ${theme.colors.surfaceHover};
    color: ${theme.colors.textPrimary};
  }

  @media (max-width: 768px) {
    justify-content: flex-start;
    padding: ${theme.spacing.md};
    margin: 0;
    border-radius: ${theme.borderRadius.md};
  }
`;

/* --- NEW: dedicated zero-gap mobile logo link --- */
const LogoLinkMobile = styled(Link)`
  display: flex;
  align-items: center;
  padding: 0;
  margin: 0;
  text-decoration: none;
`;

const MobileNav = styled.div`
  display: none;
  align-items: center;
  gap: ${theme.spacing.sm};

  @media (max-width: 1024px) {
    display: flex;
  }
`;

const MobileMenuButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: none;
  background: transparent;
  border-radius: ${theme.borderRadius.md};
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  color: ${theme.colors.textSecondary};

  &:hover {
    background-color: ${theme.colors.surfaceHover};
    color: ${theme.colors.textPrimary};
  }

  &:active {
    transform: scale(0.95);
  }
`;

const MobileActions = styled.div`
  display: none;
  align-items: center;
  gap: ${theme.spacing.xs};

  @media (max-width: 1024px) {
    display: flex;
  }
`;

const MobileLeftSection = styled.div`
  display: none;
  @media (max-width: 1024px) {
    display: flex !important;
    align-items: center;
    gap: 0px;
    margin-left: 8px;
  }
`;

const MobileLogoContainer = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  margin-left: -18px;
`;

const MobileRightSection = styled.div`
  display: none;
  @media (max-width: 1024px) {
    display: flex !important;
    align-items: center;
    gap: 6px;
    margin-right: 6px;
  }
`;

const MobileIconButton = styled.button`
  background: none;
  border: none;
  padding: 12px;
  border-radius: 8px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease, transform 0.1s ease, opacity 0.2s ease;
  min-width: 44px;
  min-height: 44px;
  touch-action: manipulation;
  opacity: 0.8;

  &:hover {
    background-color: ${theme.colors.background};
    opacity: 1;
  }
  &:active {
    transform: scale(0.95);
    background-color: ${theme.colors.primaryLight};
  }
`;


const NavBtn = styled.nav`
  display: flex;
  align-items: center;
  gap: 0.5rem;

  @media (max-width: 768px) {
    display: none;
  }
`;

const PrimaryButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing.md} ${theme.spacing.xl};
  border-radius: ${theme.borderRadius.lg};
  background: ${theme.gradients.primary};
  color: white !important;
  font-size: 0.95rem;
  font-weight: ${theme.typography.fontWeight.bold};
  text-decoration: none;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  border: none;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.35);
  position: relative;
  overflow: hidden;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent
    );
    transition: left 0.6s;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(59, 130, 246, 0.4);
    color: white !important;
  }

  &:hover:before {
    left: 100%;
  }

  &:active {
    transform: translateY(-1px);
    color: white !important;
  }

  &:visited {
    color: white !important;
  }

  &:link {
    color: white !important;
  }
`;

/* ===== Fullscreen Drawer + Backdrop (mobile) ===== */
const MobileMenuBackdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 400001;
  transition: opacity 0.3s ease-in-out, visibility 0.3s ease-in-out;
  opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
  visibility: ${({ isOpen }) => (isOpen ? "visible" : "hidden")};
  backdrop-filter: blur(2px);
  touch-action: none;
`;

const MobileMenu = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: min(90%, 400px);
  height: 100vh;
  background: ${theme.colors.surface};
  z-index: 400002;
  will-change: transform;
  backdrop-filter: blur(20px);
  border-right: 1px solid ${theme.colors.border};

  transform: ${({ isOpen }) =>
    isOpen ? "translateX(0)" : "translateX(-100%)"};
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  overflow-y: auto;
  display: flex;
  flex-direction: column;
  box-shadow: ${({ isOpen }) => (isOpen ? theme.shadows.lg : "none")};

  /* Safe area padding */
  padding-top: calc(${theme.spacing.lg} + env(safe-area-inset-top));
  padding-bottom: calc(${theme.spacing.lg} + env(safe-area-inset-bottom));

  @media (max-width: 1024px) {
    display: ${({ isOpen }) => (isOpen ? "flex" : "none")};
  }
`;

const MobileMenuHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${theme.spacing.lg} ${theme.spacing.xl};
  border-bottom: 1px solid ${theme.colors.borderLight};
  background: ${theme.colors.surface};
`;

const MobileMenuBrand = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
`;

const MobileMenuBrandIcon = styled.div`
  width: 30px;
  height: 30px;
  background: ${theme.gradients.primary};
  border-radius: ${theme.borderRadius.md};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  font-weight: ${theme.typography.fontWeight.bold};
  color: white;
`;

const MobileMenuBrandText = styled.h2`
  font-size: 1.3rem;
  font-weight: ${theme.typography.fontWeight.extrabold};
  color: ${theme.colors.textPrimary};
  margin: 0;
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: ${theme.colors.surfaceHover};
  border-radius: ${theme.borderRadius.md};
  cursor: pointer;
  color: ${theme.colors.textSecondary};
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    background: ${theme.colors.border};
    color: ${theme.colors.textPrimary};
  }
`;

const MobileMenuContent = styled.div`
  flex: 1;
  padding: ${theme.spacing.lg} 0;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
`;

const MobileMenuProfile = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  padding: ${theme.spacing.lg} ${theme.spacing.xl};
  background: ${theme.colors.surfaceHover};
  margin: ${theme.spacing.xl} ${theme.spacing.xl} ${theme.spacing.lg} ${theme.spacing.xl};
  border-radius: ${theme.borderRadius.lg};
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    background: ${theme.colors.border};
  }
`;

const MobileMenuSection = styled.div`
  padding: 0 ${theme.spacing.xl};
`;

const MobileMenuSectionTitle = styled.h3`
  font-size: 0.75rem;
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0 0 ${theme.spacing.sm} 0;
  padding: 0 ${theme.spacing.sm};
`;

const MobileMenuItem = styled(Link)`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  padding: ${theme.spacing.md} ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.md};
  color: ${theme.colors.textSecondary};
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: ${theme.typography.fontWeight.medium};
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    background-color: ${theme.colors.surfaceHover};
    color: ${theme.colors.textPrimary};
  }

  svg {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
  }
`;

const Line = styled.hr`
  border: none;
  border-top: 1px solid rgba(255, 255, 255, 0.15);
  margin: 12px 8px;
`;

const ProfileInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
`;

const ProfileName = styled.div`
  font-weight: 600;
  color: #fff;
  font-size: 16px;
`;

const ProfileEmail = styled.div`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
`;

const MenuSection = styled.div`
  padding: 6px 0;
`;

const SectionTitle = styled.div`
  padding: 6px 18px 4px;
  font-size: 12px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.6);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const PostTaskButton = styled(PrimaryButton)`
  margin: 12px 8px 8px;
  padding: 10px 18px;
  font-size: 15px;
  font-weight: 600;
  text-align: center;
  background: rgba(255, 255, 255, 0.15) !important;
  color: white !important;
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
  border-radius: 10px;

  &:hover {
    background: rgba(255, 255, 255, 0.25) !important;
    transform: translateY(-1px);
  }
`;

const NavItemLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border-radius: ${theme.borderRadius.md};
  font-size: 0.875rem;
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.textSecondary};
  text-decoration: none;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;

  &:hover {
    color: ${theme.colors.textPrimary};
    background-color: ${theme.colors.primaryLight};
  }

  &:active {
    transform: translateY(1px);
  }
`;

const SecondaryButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing.sm} ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.md};
  background: transparent;
  color: ${theme.colors.textSecondary};
  font-size: 0.875rem;
  font-weight: ${theme.typography.fontWeight.medium};
  text-decoration: none;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid ${theme.colors.border};

  &:hover {
    background-color: ${theme.colors.surfaceHover};
    color: ${theme.colors.textPrimary};
    border-color: ${theme.colors.textMuted};
  }
`;

const NotificationBubble = styled.span`
  background-color: #ef4444;
  color: white;
  font-size: 0.7rem;
  min-width: 18px;
  height: 18px;
  padding: 0 4px;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  position: absolute;
  top: -6px;
  right: -6px;
  font-weight: 700;
  border: 2px solid white;
  z-index: 10;
  box-shadow: 0 2px 8px rgba(239, 68, 68, 0.3);
`;

/* ======================= COMPONENT ======================= */
const NavBar = ({ className, ...props }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);

  const handleMobileMenu = () => setIsMobileMenuOpen((s) => !s);
  const closeMobileMenu = () => setIsMobileMenuOpen(false);
  const toggleDropdown = () => setIsDropdownVisible((s) => !s);

  // Fetch total unread count (lightweight API call)
  const fetchUnreadCount = async () => {
    if (!props.state.user.token) return;

    try {
      const response = await request("POST", API_CONSTANT_MAP.get_unread_count);

      if (
        response.data?.success &&
        typeof response.data?.data?.unread_count === "number"
      ) {
        // Dispatch to Redux store using action creator
        props.updateUnreadCount({ payload: response.data.data.unread_count });
      }
    } catch (error) {
      console.error("Failed to fetch unread count:", error);
    }
  };

  // Real-time WebSocket connection for instant unread count updates
  useTasklyWS({
    token: props.state.user.token,
    enabled: !!props.state.user.token,
    onEvent: (event) => {
      if (!event?.type) return;

      // Refresh unread count when messages are created or read
      if (
        event.type === "message.created" ||
        event.type === "conversation.read"
      ) {
        fetchUnreadCount();
      }
    },
  });

  // Fetch unread count on mount and when user changes
  useEffect(() => {
    if (props.state.user.token) {
      fetchUnreadCount();
    }
  }, [props.state.user.token]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest("#dropdown-wrapper")) {
        setIsDropdownVisible(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      const prevOverflow = document.body.style.overflow;
      const prevPosition = document.body.style.position;
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.width = "100%";
      return () => {
        document.body.style.overflow = prevOverflow;
        document.body.style.position = prevPosition;
        document.body.style.width = "";
      };
    }
  }, [isMobileMenuOpen]);

  return (
    <>
      <NavWrapper>
        <NavContainer className={className}>
          <NavContent>
            {/* Desktop Logo Section */}
            <LogoSection>
              <Brand to="/home">
                <BrandIcon>D</BrandIcon>
                <BrandText>Doitly</BrandText>
              </Brand>
            </LogoSection>

            {/* Desktop Navigation */}
            <DesktopNav>
              <NavMenu>
                <NavItemLink
                  to="/posttask"
                  onClick={(e) => {
                    if (!props.state.user.token) {
                      e.preventDefault();
                      window.location.href = "/signin";
                    }
                  }}
                  style={{
                    background: 'linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)',
                    color: 'white',
                    fontWeight: '600',
                    borderRadius: '8px',
                    padding: '8px 16px',
                    boxShadow: '0 2px 8px rgba(59, 130, 246, 0.25)',
                    transform: 'scale(1)',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    marginRight: '12px'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'scale(1.02) translateY(-1px)';
                    e.target.style.boxShadow = '0 4px 15px rgba(59, 130, 246, 0.35)';
                    e.target.style.color = 'white';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'scale(1) translateY(0)';
                    e.target.style.boxShadow = '0 2px 8px rgba(59, 130, 246, 0.25)';
                    e.target.style.color = 'white';
                  }}
                >
                  Post a task
                </NavItemLink>
                <NavItemLink to="/tasks">Browse tasks</NavItemLink>
                <NavItemLink to="/howitworks">How it works</NavItemLink>
                {props.state.user.token && (
                  <NavItemLink to="/dashboard">My Dashboard</NavItemLink>
                )}
              </NavMenu>

              <NavActions>
                {!props.state.user.token ? (
                  <>
                    <SecondaryButton to="/signin">Login</SecondaryButton>
                    <PrimaryButton to="/signup">Sign up</PrimaryButton>
                  </>
                ) : (
                  <>
                    {/* Messages icon */}
                    <NavItem
                      path="/chat"
                      content={
                        <IconBuilder icon={<FiMessageSquare size={20} />} />
                      }
                      count={props.state.user.unread_message_count}
                    />
                    <div id="dropdown-wrapper">
                      <NavItem2
                        path="#"
                        content={
                          <RoundedImage
                            imageHeight={32}
                            imageWidth={32}
                            borderRadiusPct={50}
                            image={
                              props.user?.avatar
                                ? props.user.avatar
                                : unknown_gw
                            }
                          />
                        }
                        onClick={toggleDropdown}
                      >
                        <Dropdown
                          user={{ ...props.user }}
                          isDropdownVisible={isDropdownVisible}
                        />
                      </NavItem2>
                    </div>
                  </>
                )}
              </NavActions>
            </DesktopNav>

            {/* Mobile Navigation */}
            <MobileNav>
              <MobileMenuButton
                onClick={handleMobileMenu}
                aria-label="Open menu"
              >
                <FiMenu size={20} />
              </MobileMenuButton>
              <Brand to="/home">
                <BrandIcon>D</BrandIcon>
                <BrandText>Doitly</BrandText>
              </Brand>
            </MobileNav>

            {/* Mobile Actions */}
            <MobileActions>
              <MobileMenuButton
                aria-label="Browse tasks"
                onClick={() => (window.location.href = "/tasks")}
              >
                <FiSearch size={20} />
              </MobileMenuButton>

              <MobileMenuButton
                aria-label="Post task"
                onClick={() => {
                  if (!props.state.user.token) {
                    window.location.href = "/signin";
                  } else {
                    window.location.href = "/posttask";
                  }
                }}
                style={{
                  background: 'linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)',
                  color: 'white',
                  borderRadius: '50%',
                  width: '32px',
                  height: '32px',
                  boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)',
                  border: '1px solid white',
                  transform: 'scale(1)',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'scale(1.05) translateY(-1px)';
                  e.target.style.boxShadow = '0 6px 18px rgba(59, 130, 246, 0.5)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'scale(1) translateY(0)';
                  e.target.style.boxShadow = '0 3px 12px rgba(59, 130, 246, 0.4)';
                }}
              >
                <FiPlus size={16} />
              </MobileMenuButton>


            </MobileActions>
          </NavContent>
        </NavContainer>
      </NavWrapper>

      {/* Mobile Menu - Outside NavWrapper to avoid clipping */}
      <MobileMenuBackdrop isOpen={isMobileMenuOpen} onClick={closeMobileMenu} />

      <MobileMenu isOpen={isMobileMenuOpen} role="dialog" aria-modal="true">
        <MobileMenuHeader>
          <MobileMenuBrand>
            <MobileMenuBrandIcon>D</MobileMenuBrandIcon>
            <MobileMenuBrandText>Doitly</MobileMenuBrandText>
          </MobileMenuBrand>
          <CloseButton onClick={closeMobileMenu} aria-label="Close">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </CloseButton>
        </MobileMenuHeader>

        {props.state.user.token && (
          <MobileMenuProfile
            onClick={() => {
              closeMobileMenu();
              window.location.href = "/account";
            }}
          >
            <RoundedImage
              imageHeight={40}
              imageWidth={40}
              borderRadiusPct={50}
              image={props.user?.avatar ? props.user.avatar : unknown_gw}
            />
            <div>
              <div
                style={{
                  fontWeight: theme.typography.fontWeight.semibold,
                  color: theme.colors.textPrimary,
                  fontSize: "0.9rem",
                  textTransform: "capitalize",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                {props.user?.first_name && props.user?.last_name
                  ? `${props.user.first_name} ${props.user.last_name}`
                  : props.user?.username || "User"}
                <VerificationBadge 
                  isVerified={props.user?.email_verified} 
                  size="12px" 
                  spacing="3px"
                />
              </div>
              <div
                style={{
                  fontSize: "0.8rem",
                  color: theme.colors.textMuted,
                  marginTop: "2px",
                }}
              >
                {props.user?.email}
              </div>
            </div>
          </MobileMenuProfile>
        )}

        <MobileMenuContent>
          {props.state.user.token ? (
            <>
              <div style={{ padding: `0 ${theme.spacing.xl}`, marginBottom: theme.spacing.lg }}>
                <PrimaryButton
                  to="/posttask"
                  onClick={(e) => {
                    if (!props.state.user.token) {
                      e.preventDefault();
                      closeMobileMenu();
                      window.location.href = "/signin";
                      return;
                    }
                    closeMobileMenu();
                  }}
                  style={{ width: "100%", justifyContent: "center", marginBottom: theme.spacing.md }}
                >
                  Post a Task
                </PrimaryButton>
              </div>
              <MobileMenuSection>
                <MobileMenuSectionTitle>Navigate</MobileMenuSectionTitle>
                <MobileMenuItem to="/" onClick={closeMobileMenu}>
                  <FiHome />
                  <span>Home</span>
                </MobileMenuItem>
                <MobileMenuItem to="/tasks" onClick={closeMobileMenu}>
                  <FiSearch />
                  <span>Browse Tasks</span>
                </MobileMenuItem>
                <MobileMenuItem to="/chat" onClick={closeMobileMenu}>
                  <FiMessageSquare />
                  <span>Messages</span>
                </MobileMenuItem>
              </MobileMenuSection>

              <MobileMenuSection>
                <MobileMenuSectionTitle>My Account</MobileMenuSectionTitle>
                <MobileMenuItem to="/dashboard" onClick={closeMobileMenu}>
                  <FiGrid />
                  <span>My Dashboard</span>
                </MobileMenuItem>
                <MobileMenuItem to="/account" onClick={closeMobileMenu}>
                  <FiSettings />
                  <span>Settings</span>
                </MobileMenuItem>
              </MobileMenuSection>

              <MobileMenuSection>
                <MobileMenuSectionTitle>Support</MobileMenuSectionTitle>
                <MobileMenuItem to="/howitworks" onClick={closeMobileMenu}>
                  <FiHelpCircle />
                  <span>How it works</span>
                </MobileMenuItem>
                <MobileMenuItem to="/signout" onClick={closeMobileMenu}>
                  <FiLogOut />
                  <span>Sign Out</span>
                </MobileMenuItem>
              </MobileMenuSection>
            </>
          ) : (
            <>
              <div style={{ padding: `0 ${theme.spacing.xl}`, marginBottom: theme.spacing.lg }}>
                <PrimaryButton
                  to="/posttask"
                  onClick={(e) => {
                    e.preventDefault();
                    closeMobileMenu();
                    window.location.href = "/signin";
                  }}
                  style={{ width: "100%", justifyContent: "center", marginBottom: theme.spacing.md }}
                >
                  Post a Task
                </PrimaryButton>
              </div>
              <MobileMenuSection>
                <MobileMenuSectionTitle>Browse</MobileMenuSectionTitle>
                <MobileMenuItem to="/" onClick={closeMobileMenu}>
                  <FiHome />
                  <span>Home</span>
                </MobileMenuItem>
                <MobileMenuItem to="/tasks" onClick={closeMobileMenu}>
                  <FiSearch />
                  <span>Browse Tasks</span>
                </MobileMenuItem>
                <MobileMenuItem to="/howitworks" onClick={closeMobileMenu}>
                  <FiHelpCircle />
                  <span>How it works</span>
                </MobileMenuItem>
              </MobileMenuSection>

              <MobileMenuSection>
                <MobileMenuSectionTitle>Account</MobileMenuSectionTitle>
                <MobileMenuItem to="/signin" onClick={closeMobileMenu}>
                  <FiUser />
                  <span>Sign In</span>
                </MobileMenuItem>
              </MobileMenuSection>
            </>
          )}
        </MobileMenuContent>
      </MobileMenu>
    </>
  );
};

/* ======================= HELPERS ======================= */
const Dropdown = (props) => {
  const navigate = useNavigate();

  const handleItemClick = (path) => (e) => {
    e.preventDefault();
    navigate(path);
  };

  return (
    <UserDropdown id="dropdown" visible={props.isDropdownVisible}>
      <DropdownHeader>
        <DropdownUserInfo>
          <RoundedImage
            imageHeight={36}
            imageWidth={36}
            borderRadiusPct={50}
            image={props.user?.avatar ? props.user.avatar : unknown_gw}
          />
          <DropdownUserDetails>
            <DropdownUserName>
              {props.user.first_name} {props.user.last_name}
              <VerificationBadge 
                isVerified={props.user?.email_verified} 
                size="12px" 
                spacing="4px"
              />
            </DropdownUserName>
            <DropdownUserEmail>{props.user?.email}</DropdownUserEmail>
          </DropdownUserDetails>
        </DropdownUserInfo>
      </DropdownHeader>

      <DropdownMenuItem onClick={handleItemClick("/account")}>
        <FiSettings />
        <span>Account Settings</span>
      </DropdownMenuItem>

      <DropdownMenuItem onClick={handleItemClick("/dashboard")}>
        <FiGrid />
        <span>My Dashboard</span>
      </DropdownMenuItem>

      <DropdownMenuItem onClick={handleItemClick("/signout")}>
        <FiLogOut />
        <span>Sign Out</span>
      </DropdownMenuItem>
    </UserDropdown>
  );
};

const NavItem2 = (props) => {
  const handleClick = (event) => {
    props.onClick();
    event.stopPropagation();
  };
  return (
    <div onClick={handleClick}>
      <NavItemLink to={props.path}>{props.content}</NavItemLink>
      {props.children}
    </div>
  );
};

const NavItem = (props) => {
  const navigate = useNavigate();
  const handleClick = (event) => {
    event.preventDefault();
    if (props.onClick) props.onClick();
    navigate(props.path);
  };

  const shouldShowBubble =
    props.count !== undefined &&
    props.count !== null &&
    Number(props.count) > 0;

  return (
    <NavActionButton onClick={handleClick}>
      {props.content}
      {shouldShowBubble && (
        <NotificationBubble>{props.count}</NotificationBubble>
      )}
    </NavActionButton>
  );
};

const IconBuilder = (props) => {
  return (
    <IconContainer>
      <span>{props.icon}</span>
      <span>{props.icon_text}</span>
    </IconContainer>
  );
};


const mapStateToProps = (state) => ({
  state,
  user: state.user, // Add direct access to user data
});
const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      search,
      zipcode,
      location,
      distance,
      categories,
      sortBy,
      itemConditions,
      zipcodeDistChanged,
      updateUnreadCount,
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(NavBar);
